// OWNER: FRONTEND-ADMIN. Small building blocks shared by the admin pages (on top of components/ui.tsx).
import { Fragment, type ComponentChildren } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { Modal } from '../components/ui';
import { errorMessage } from '../api';
import type { Problem } from './validate';
import type { ColumnSpec } from './layout';
import { apiErrorView, type ApiErrorView } from './errors';
import './admin.css';

/** Load data on mount / when deps change. Keeps the last good data while reloading; never swallows errors. */
export function useLoad<T>(fn: () => Promise<T>, deps: unknown[] = []): {
  data: T | null; error: string | null; loading: boolean; reload: () => Promise<void>; setData: (d: T) => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);
  const reload = useCallback(async () => {
    const my = ++seq.current;
    setLoading(true);
    try {
      const d = await fn();
      if (my === seq.current) { setData(d); setError(null); }
    } catch (e) {
      if (my === seq.current) setError(errorMessage(e));
    } finally {
      if (my === seq.current) setLoading(false);
    }
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [reload]);
  return { data, error, loading, reload, setData };
}

export function Tabs<K extends string>(props: { tabs: Array<{ key: K; label: string; badge?: string | number | null }>; active: K; onChange: (k: K) => void }) {
  return (
    <div class="adm-tabs" role="tablist">
      {props.tabs.map((t) => (
        <button type="button" role="tab" aria-selected={t.key === props.active} class={`adm-tab${t.key === props.active ? ' active' : ''}`} onClick={() => props.onChange(t.key)}>
          {t.label}{t.badge !== undefined && t.badge !== null && t.badge !== '' ? <span class="chip">{t.badge}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function Field(props: { label: string; hint?: ComponentChildren; children: ComponentChildren; style?: string }) {
  return (
    <label class="adm-field" style={props.style}>
      <span>{props.label}</span>
      {props.children}
      {props.hint ? <span class="adm-hint">{props.hint}</span> : null}
    </label>
  );
}

export function Check(props: { checked: boolean; onChange: (v: boolean) => void; label: ComponentChildren; disabled?: boolean; title?: string }) {
  return (
    <label class="adm-check" title={props.title}>
      <input type="checkbox" checked={props.checked} disabled={props.disabled} onChange={(e) => props.onChange((e.target as HTMLInputElement).checked)} />
      <span>{props.label}</span>
    </label>
  );
}

export function ProblemList(props: { problems: Problem[]; max?: number }) {
  if (props.problems.length === 0) return null;
  const sorted = [...props.problems].sort((a, b) => (a.level === b.level ? 0 : a.level === 'error' ? -1 : 1));
  const shown = props.max ? sorted.slice(0, props.max) : sorted;
  return (
    <ul class="adm-problems">
      {shown.map((p) => <li class={p.level}>{p.message}</li>)}
      {props.max && sorted.length > props.max ? <li class="warn">…and {sorted.length - props.max} more.</li> : null}
    </ul>
  );
}

export function Stat(props: { label: string; value: ComponentChildren }) {
  return <div class="adm-stat"><b>{props.value}</b><span>{props.label}</span></div>;
}

/** Yes/no confirmation. The confirm button can require a typed note (status changes, DNC adds). */
export function ConfirmModal(props: {
  title: string; body: ComponentChildren; confirmLabel: string; danger?: boolean;
  noteLabel?: string; notePlaceholder?: string; noteRequired?: boolean; noteInitial?: string;
  onConfirm: (note: string) => Promise<void>; onClose: () => void;
}) {
  const [note, setNote] = useState(props.noteInitial ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiErrorView | null>(null);
  const needNote = !!props.noteRequired && !note.trim();
  const go = async () => {
    if (needNote) { setError(apiErrorView(new Error(`${props.noteLabel ?? 'A note'} is required.`))); return; }
    setBusy(true); setError(null);
    // the server's message verbatim, plus details.problems (go-live) and Twilio codes when it sends them
    try { await props.onConfirm(note.trim()); props.onClose(); } catch (e) { setError(apiErrorView(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title={props.title} onClose={props.onClose}>
      <div class="stack">
        <div>{props.body}</div>
        {props.noteLabel ? (
          <Field label={props.noteLabel + (props.noteRequired ? ' (required)' : '')}>
            <textarea rows={3} value={note} placeholder={props.notePlaceholder} onInput={(e) => setNote((e.target as HTMLTextAreaElement).value)} />
          </Field>
        ) : null}
        <ApiErrorBox error={error} />
        <div class="row" style="justify-content:flex-end">
          <button class="btn" onClick={props.onClose} disabled={busy}>Cancel</button>
          <button class={`btn ${props.danger ? 'btn-danger' : 'btn-ink'}`} onClick={go} disabled={busy || needNote}>{busy ? 'Working…' : props.confirmLabel}</button>
        </div>
      </div>
    </Modal>
  );
}

/** An API failure as the server sent it: the message verbatim, then every `details.problems` line (go-live) and any
 *  Twilio code / link or SIP trunk SID. Plain `Error`s show just their message. */
export function ApiErrorBox(props: { error: ApiErrorView | null }) {
  const e = props.error;
  if (!e) return null;
  return (
    <div class="chip chip-bad" role="alert" style="display:block;padding:8px 10px;white-space:normal;font-size:13px">
      <div style="white-space:pre-wrap">{e.message}</div>
      {e.problems.length ? <ul class="adm-err-list">{e.problems.map((p) => <li>{p}</li>)}</ul> : null}
      {e.extra.map((x) => (
        <div class="adm-err-extra">{/^https:\/\//.test(x) ? <a href={x} target="_blank" rel="noopener noreferrer">{x}</a> : x}</div>
      ))}
    </div>
  );
}

/** Save a text file in the browser (CSV exports, rejected rows). BOM so Excel reads UTF-8 names correctly. */
export function downloadText(filename: string, text: string, mime = 'text/csv'): void {
  const blob = new Blob(['﻿', text], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function Progress(props: { done: number; total: number }) {
  const pct = props.total > 0 ? Math.min(100, Math.round((props.done / props.total) * 100)) : 0;
  return <div class="adm-progress" role="progressbar" aria-valuemin={0} aria-valuemax={props.total} aria-valuenow={props.done}><div style={`width:${pct}%`} /></div>;
}

const STATUS_CHIP: Record<string, string> = {
  live: 'chip-ok', active: 'chip-ok', approved: 'chip-ok',
  draft: '', ramping: 'chip-info', pending: 'chip-info',
  paused: 'chip-warn', resting: 'chip-warn',
  done: '', retired: '', flagged: 'chip-bad', rejected: 'chip-bad',
};
export function StatusChip(props: { status: string }) {
  return <span class={`chip ${STATUS_CHIP[props.status] ?? ''}`}>{props.status.replace(/_/g, ' ')}</span>;
}

/** Repeatable list editor: renders each item with move up/down/remove controls and an Add button. */
export function ListEditor<T>(props: {
  items: T[];
  onChange: (items: T[]) => void;
  render: (item: T, update: (next: T) => void, index: number) => ComponentChildren;
  title: (item: T, index: number) => string;
  make: () => T;
  addLabel: string;
  disabled?: boolean;
}) {
  const { items, onChange } = props;
  const set = (i: number, next: T) => onChange(items.map((x, j) => (j === i ? next : x)));
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const copy = [...items];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy);
  };
  return (
    <div class="stack">
      {items.map((item, i) => (
        <div class="adm-block">
          <div class="adm-block-head">
            <span class="eyebrow">{i + 1}.</span>
            <b class="grow" style="font-size:13px">{props.title(item, i)}</b>
            {props.disabled ? null : (
              <>
                <button type="button" class="btn btn-sm" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up">↑</button>
                <button type="button" class="btn btn-sm" disabled={i === items.length - 1} onClick={() => move(i, 1)} aria-label="Move down">↓</button>
                <button type="button" class="btn btn-sm" onClick={() => onChange(items.filter((_, j) => j !== i))} aria-label="Remove">Remove</button>
              </>
            )}
          </div>
          {props.render(item, (next) => set(i, next), i)}
        </div>
      ))}
      {props.disabled ? null : <div><button type="button" class="btn btn-sm" onClick={() => onChange([...items, props.make()])}>+ {props.addLabel}</button></div>}
    </div>
  );
}

/** `<colgroup>` + header row for a fixed-layout table (admin.css .adm-fixed) from a column spec in layout.ts. */
export function FixedHead(props: { cols: ReadonlyArray<ColumnSpec> }) {
  return (
    <>
      <colgroup>{props.cols.map((c) => <col style={`width:${c.width}px`} />)}</colgroup>
      <thead><tr>
        {props.cols.map((c) => (
          <th class={c.num ? 'num' : undefined} title={c.title} scope="col">
            {c.label || (c.srLabel ? <span class="adm-sr">{c.srLabel}</span> : null)}
          </th>
        ))}
      </tr></thead>
    </>
  );
}

export interface MenuItem { label: string; onSelect: () => void; danger?: boolean; separatorBefore?: boolean; title?: string }

/**
 * A small action menu for a table row. The list is position:fixed next to the button, so it is never clipped by a
 * scrolling table wrapper. Keyboard: Enter/Space opens and focuses the first item, ↑/↓/Home/End move, Esc closes
 * and returns focus to the button, Tab closes.
 */
export function RowMenu(props: { label: string; ariaLabel: string; items: MenuItem[] }) {
  const [pos, setPos] = useState<{ right: number; top?: number; bottom?: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const close = (refocus: boolean) => { setPos(null); if (refocus) btn.current?.focus(); };

  const toggle = () => {
    if (pos) { close(false); return; }
    const b = btn.current;
    if (!b) return;
    const r = b.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const estHeight = props.items.length * 34 + 16;
    const openUp = r.bottom + 4 + estHeight > window.innerHeight && r.top - 4 - estHeight > 0;
    setPos(openUp ? { right: vw - r.right, bottom: window.innerHeight - r.top + 4 } : { right: vw - r.right, top: r.bottom + 4 });
  };

  useEffect(() => {
    if (!pos) return;
    const items = () => [...(menu.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])];
    items()[0]?.focus({ preventScroll: true });
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!menu.current?.contains(t) && !btn.current?.contains(t)) close(false);
    };
    const onKey = (e: KeyboardEvent) => {
      const list = items();
      const i = list.indexOf(document.activeElement as HTMLButtonElement);
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(true); }
      else if (e.key === 'Tab') close(false);
      else if (e.key === 'ArrowDown') { e.preventDefault(); list[(i + 1) % list.length]?.focus(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); list[(i - 1 + list.length) % list.length]?.focus(); }
      else if (e.key === 'Home') { e.preventDefault(); list[0]?.focus(); }
      else if (e.key === 'End') { e.preventDefault(); list[list.length - 1]?.focus(); }
    };
    const onMove = (e: Event) => { if (!menu.current?.contains(e.target as Node)) close(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [pos !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  const style = pos ? `right:${pos.right}px;${pos.top !== undefined ? `top:${pos.top}px` : `bottom:${pos.bottom}px`}` : '';
  return (
    <>
      <button ref={btn} type="button" class="btn btn-sm adm-menu-btn" aria-haspopup="menu" aria-expanded={pos !== null} aria-label={props.ariaLabel}
        onClick={toggle}>{props.label} <span aria-hidden="true">▾</span></button>
      {pos ? (
        <div ref={menu} class="adm-menu" role="menu" aria-label={props.ariaLabel} style={style}>
          {props.items.map((it, i) => (
            <Fragment key={i}>
              {it.separatorBefore ? <div class="adm-menu-sep" role="separator" /> : null}
              <button type="button" role="menuitem" class={`adm-menu-item${it.danger ? ' danger' : ''}`} title={it.title}
                onClick={() => { close(false); it.onSelect(); }}>{it.label}</button>
            </Fragment>
          ))}
        </div>
      ) : null}
    </>
  );
}

export const inputValue = (e: Event): string => (e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value;

/** $0.48, $1.15, $0.008 — a third decimal only when the amount has a fraction of a cent. */
export function money(n: number): string {
  const wholeCents = Math.abs(Math.round(n * 100) - n * 100) < 1e-6;
  return `$${n.toFixed(wholeCents ? 2 : 3)}`;
}

/** Warn before leaving the page with unsaved edits (browser close/reload). */
export function useUnsavedWarning(dirty: boolean): void {
  useEffect(() => {
    if (!dirty) return;
    const on = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', on);
    return () => window.removeEventListener('beforeunload', on);
  }, [dirty]);
}
